import { useState } from 'react'
import { Box, Button, Container, Field, Heading, Input, Text, VStack } from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { lobbyAttendeeApi } from '../../lib/api'

/** The exact copy from the design — one outcome state for every 202. */
const CONFIRMATION =
  "If that address is registered for an active lobby day, we've sent your link. " +
  'Check your inbox and spam folder. Nothing after a few minutes? Ask your organizer for a new link.'

export function FindMyLinkPage() {
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { mutate, isPending } = useMutation({
    mutationFn: (value: string) => lobbyAttendeeApi.resendLink(value),
    // Deliberately ignores the response body: branching on it would recreate the oracle
    // the constant 202 exists to remove.
    onSuccess: () => { setError(null); setSent(true) },
    onError: (err: { response?: { status?: number; data?: { message?: string } } }) => {
      const status = err?.response?.status
      if (status === 429) setError('Too many requests from this network. Wait a few minutes and try again.')
      else if (status === 400) setError(err?.response?.data?.message ?? 'That address does not look right.')
      else setError('Something went wrong. Please try again.')
    },
  })

  if (sent) {
    return (
      <Container maxW="26rem" py={16}>
        <VStack gap={6} align="stretch">
          <Heading size="lg">Check your email</Heading>
          <Text color="fg.muted">{CONFIRMATION}</Text>
          <Button asChild variant="outline" minH="44px">
            <RouterLink to="/">Back to start</RouterLink>
          </Button>
        </VStack>
      </Container>
    )
  }

  return (
    <Container maxW="26rem" py={16}>
      <VStack gap={6} align="stretch">
        <Box>
          <Heading size="lg" mb={2}>Find my link</Heading>
          <Text color="fg.muted">
            Enter the email address your organizer has for you and we'll send your schedule link.
          </Text>
        </Box>

        <Field.Root>
          <Field.Label>Email address</Field.Label>
          <Input
            type="email"
            value={email}
            minH="44px"
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />
        </Field.Root>

        {!!error && <Text color="danger.fg">{error}</Text>}

        <Button
          colorPalette="brand"
          minH="44px"
          loading={isPending}
          disabled={email.trim().length === 0}
          onClick={() => mutate(email.trim())}
        >
          Email me my link
        </Button>

        <Button asChild variant="ghost" minH="44px">
          <RouterLink to="/">Back to start</RouterLink>
        </Button>
      </VStack>
    </Container>
  )
}
